<template>
  <div class="revenue-page">
    <!-- 顶部选择栏 -->
    <div class="header-bar">
      <div class="org-select-card">
        <van-dropdown-menu
          class="custom-dropdown-menu"
          active-color="#22B894"
        >
          <van-dropdown-item
            v-model="currentOrg"
            :options="orgOptions"
            title-class="custom-dropdown-title"
            @change="handleOrgChange"
          />
        </van-dropdown-menu>
      </div>
      <div class="date-filter-btn" @click="showDateFilter = true">
        <span class="date-text">{{ dateRangeText }}</span>
        <van-icon name="calendar-o" />
      </div>
    </div>

    <!-- 当月统计卡片 -->
    <div class="month-summary-cards">
      <div class="summary-card income">
        <div class="card-label">当月实收</div>
        <div class="card-value">
          <span class="value-number">{{ formatMoney(monthSummary.currentIncome) }}</span>
          <span class="value-unit">元</span>
        </div>
        <div class="card-compare" v-if="monthSummary.incomeCompare !== undefined">
          <span :class="monthSummary.incomeCompare >= 0 ? 'up' : 'down'">
            {{ monthSummary.incomeCompare >= 0 ? '↑' : '↓' }}
            {{ Math.abs(monthSummary.incomeCompare) }}%
          </span>
          <span class="compare-text">环比</span>
        </div>
      </div>
      <div class="summary-card refund">
        <div class="card-label">当月实退</div>
        <div class="card-value">
          <span class="value-number">{{ formatMoney(monthSummary.currentRefund) }}</span>
          <span class="value-unit">元</span>
        </div>
        <div class="card-compare" v-if="monthSummary.refundCompare !== undefined">
          <span :class="monthSummary.refundCompare <= 0 ? 'up' : 'down'">
            {{ monthSummary.refundCompare <= 0 ? '↑' : '↓' }}
            {{ Math.abs(monthSummary.refundCompare) }}%
          </span>
          <span class="compare-text">环比</span>
        </div>
      </div>
    </div>

    <!-- 时间范围内统计卡片 -->
    <div class="period-stats-section">
      <div class="section-title">时间范围内统计</div>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value">{{ formatMoney(periodStats.actualIncome) }}</div>
          <div class="stat-label">实收金额</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ formatMoney(periodStats.actualRefund) }}</div>
          <div class="stat-label">实退金额</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ periodStats.signUpCount || 0 }}</div>
          <div class="stat-label">报名人数</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ periodStats.refundCount || 0 }}</div>
          <div class="stat-label">退费人数</div>
        </div>
      </div>
    </div>

    <!-- 分校区统计柱状图 -->
    <div class="chart-section" v-if="branchData.length > 0">
      <div class="section-title">
        分校区统计
        <span class="subtitle">（直营书院柱状图样式）</span>
      </div>
      <div ref="branchChartRef" class="chart-container"></div>
    </div>

    <!-- 报课金额学部统计堆叠图 -->
    <div class="chart-section" v-if="subjectData.length > 0">
      <div class="section-title">报课金额学部统计</div>
      <div class="chart-legend-inline">
        <span v-for="(item, index) in subjectLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: subjectColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div ref="subjectChartRef" class="chart-container"></div>
    </div>

    <!-- 报课金额分类统计（班课/一对一） -->
    <div class="chart-section" v-if="courseTypeData.length > 0">
      <div class="section-title">报课金额分类统计</div>
      <div class="chart-legend-inline">
        <span v-for="(item, index) in courseTypeLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: courseTypeColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div ref="courseTypeChartRef" class="chart-container"></div>
    </div>

    <!-- 报课金额季节分类统计 -->
    <div class="chart-section" v-if="seasonData.length > 0">
      <div class="section-title">报课金额季节分类统计</div>
      <div class="chart-legend-inline">
        <span v-for="(item, index) in seasonLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: seasonColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div ref="seasonChartRef" class="chart-container"></div>
    </div>

    <!-- 报名分类统计 -->
    <div class="chart-section" v-if="signUpCategoryData.length > 0">
      <div class="section-title">报名分类统计</div>
      <div class="chart-legend-inline">
        <span v-for="(item, index) in signUpCategoryLegend" :key="index" class="legend-item">
          <span class="legend-dot" :style="{ background: signUpCategoryColors[index] }"></span>
          {{ item }}
        </span>
      </div>
      <div ref="signUpChartRef" class="chart-container"></div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-overlay">
      <van-loading type="spinner" color="#22B894">数据加载中...</van-loading>
    </div>

    <!-- 空数据提示 -->
    <EmptyData v-if="!loading && !hasAnyData" text="暂无营收数据" />

    <!-- 日期筛选弹窗 -->
    <van-popup
      v-model:show="showDateFilter"
      position="bottom"
      round
      :style="{ height: 'auto' }"
    >
      <div class="date-filter-popup">
        <div class="popup-header">
          <span class="cancel-btn" @click="showDateFilter = false">取消</span>
          <span class="popup-title">选择时间范围</span>
          <span class="confirm-btn" @click="confirmDateFilter">确认</span>
        </div>
        <div class="quick-date-btns">
          <span
            v-for="item in quickDateOptions"
            :key="item.value"
            :class="['quick-btn', { active: selectedQuickType === item.value }]"
            @click="selectQuickDate(item.value)"
          >
            {{ item.label }}
          </span>
        </div>
        <div class="custom-date-btn" @click="showCalendar = true">
          <van-icon name="calendar-o" />
          <span>自定义时间</span>
        </div>
        <div class="current-range">
          <span>{{ dateRangeText }}</span>
        </div>
      </div>
    </van-popup>

    <!-- 日历选择 -->
    <van-calendar
      v-model:show="showCalendar"
      title="选择时间"
      type="range"
      color="#22B894"
      :round="false"
      :min-date="minDate"
      allow-same-day
      @confirm="handleCalendarConfirm"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import * as echarts from 'echarts'
import EmptyData from '@/components/EmptyData.vue'
import dayjs from 'dayjs'
import { getMonthSummary, getBranchRevenue, getSubjectRevenue, getCourseTypeRevenue, getSeasonRevenue, getSignUpCategory } from '@/api/revenue'

// 机构选择
const currentOrg = ref('')
const orgOptions = ref([
  { text: '全部校区', value: '' },
  { text: '铁一旗舰校', value: '1' },
  { text: '工大旗舰校', value: '2' },
  { text: '青羊分校', value: '3' }
])

// 日期筛选
const showDateFilter = ref(false)
const showCalendar = ref(false)
const selectedQuickType = ref('currentMonth')
const customDateRange = ref([])

const quickDateOptions = [
  { label: '本月', value: 'currentMonth' },
  { label: '上月', value: 'prevMonth' },
  { label: '本季度', value: 'currentQuarter' },
  { label: '本年', value: 'currentYear' }
]

const dateRange = ref([])

const dateRangeText = computed(() => {
  if (customDateRange.value.length === 2) {
    return `${customDateRange.value[0]} ~ ${customDateRange.value[1]}`
  }
  if (dateRange.value.length === 2) {
    return `${dateRange.value[0]} ~ ${dateRange.value[1]}`
  }
  return getDefaultDateRange()
})

function getDefaultDateRange() {
  const start = dayjs().startOf('month').format('YYYY-MM-DD')
  const end = dayjs().endOf('month').format('YYYY-MM-DD')
  return `${start} ~ ${end}`
}

function getDateRangeByType(type) {
  const now = dayjs()
  switch (type) {
    case 'currentMonth':
      return [now.startOf('month').format('YYYY-MM-DD'), now.endOf('month').format('YYYY-MM-DD')]
    case 'prevMonth':
      return [now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')]
    case 'currentQuarter':
      return [now.startOf('quarter').format('YYYY-MM-DD'), now.endOf('quarter').format('YYYY-MM-DD')]
    case 'currentYear':
      return [now.startOf('year').format('YYYY-MM-DD'), now.endOf('year').format('YYYY-MM-DD')]
    default:
      return [now.startOf('month').format('YYYY-MM-DD'), now.endOf('month').format('YYYY-MM-DD')]
  }
}

function selectQuickDate(type) {
  selectedQuickType.value = type
  customDateRange.value = []
  dateRange.value = getDateRangeByType(type)
}

function handleCalendarConfirm(values) {
  showCalendar.value = false
  if (Array.isArray(values) && values.length === 2) {
    const [start, end] = values
    customDateRange.value = [dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD')]
    selectedQuickType.value = ''
  }
}

function confirmDateFilter() {
  showDateFilter.value = false
  loadAllData()
}

function handleOrgChange(value) {
  loadAllData()
}

const minDate = dayjs().subtract(1, 'year').toDate()

// 数据状态
const loading = ref(false)
const monthSummary = reactive({
  currentIncome: 0,
  currentRefund: 0,
  incomeCompare: 0,
  refundCompare: 0
})
const periodStats = reactive({
  actualIncome: 0,
  actualRefund: 0,
  signUpCount: 0,
  refundCount: 0
})

// 图表数据
const branchData = ref([])
const subjectData = ref([])
const courseTypeData = ref([])
const seasonData = ref([])
const signUpCategoryData = ref([])

// 图表实例
let branchChart = null
let subjectChart = null
let courseTypeChart = null
let seasonChart = null
let signUpChart = null

// 图表容器
const branchChartRef = ref(null)
const subjectChartRef = ref(null)
const courseTypeChartRef = ref(null)
const seasonChartRef = ref(null)
const signUpChartRef = ref(null)

// 图例
const subjectLegend = ['小学', '初中', '高中']
const subjectColors = ['#5470C6', '#91CC75', '#FAC858']
const courseTypeLegend = ['班课', '1对1']
const courseTypeColors = ['#5470C6', '#EE6666']
const seasonLegend = ['春季', '暑期', '秋季', '寒假']
const seasonColors = ['#91CC75', '#FAC858', '#5470C6', '#EE6666']
const signUpCategoryLegend = ['新签', '扩科', '续费']
const signUpCategoryColors = ['#5470C6', '#91CC75', '#FAC858']

const hasAnyData = computed(() => {
  return branchData.value.length > 0 || subjectData.value.length > 0 ||
    courseTypeData.value.length > 0 || seasonData.value.length > 0 ||
    signUpCategoryData.value.length > 0
})

// 格式化金额
function formatMoney(value) {
  if (!value && value !== 0) return '0.00'
  const num = parseFloat(value)
  if (num >= 10000) {
    return (num / 10000).toFixed(2) + '万'
  }
  return num.toFixed(2)
}

// 获取请求参数
function getRequestParams() {
  const range = customDateRange.value.length > 0 ? customDateRange.value : dateRange.value
  const params = {
    startDate: range[0] || dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: range[1] || dayjs().endOf('month').format('YYYY-MM-DD')
  }
  if (currentOrg.value) {
    params.orgId = currentOrg.value
  }
  return params
}

// 加载所有数据
async function loadAllData() {
  loading.value = true
  const params = getRequestParams()

  try {
    await Promise.all([
      loadMonthSummary(params),
      loadPeriodStats(params),
      loadBranchData(params),
      loadSubjectData(params),
      loadCourseTypeData(params),
      loadSeasonData(params),
      loadSignUpCategoryData(params)
    ])
  } catch (error) {
    console.error('加载数据失败', error)
  } finally {
    loading.value = false
  }
}

async function loadMonthSummary(params) {
  try {
    const res = await getMonthSummary(params)
    if (res.code === '00' && res.data) {
      monthSummary.currentIncome = res.data.currentIncome || 0
      monthSummary.currentRefund = res.data.currentRefund || 0
      monthSummary.incomeCompare = res.data.incomeCompare || 0
      monthSummary.refundCompare = res.data.refundCompare || 0
    }
  } catch (error) {
    // 使用模拟数据
    monthSummary.currentIncome = 1256800
    monthSummary.currentRefund = 98600
    monthSummary.incomeCompare = 15.8
    monthSummary.refundCompare = -5.2
  }
}

async function loadPeriodStats(params) {
  try {
    const res = await getBranchRevenue(params)
    if (res.code === '00' && res.data) {
      periodStats.actualIncome = res.data.actualIncome || 0
      periodStats.actualRefund = res.data.actualRefund || 0
      periodStats.signUpCount = res.data.signUpCount || 0
      periodStats.refundCount = res.data.refundCount || 0
    }
  } catch (error) {
    // 使用模拟数据
    periodStats.actualIncome = 5680000
    periodStats.actualRefund = 325000
    periodStats.signUpCount = 486
    periodStats.refundCount = 32
  }
}

async function loadBranchData(params) {
  try {
    const res = await getBranchRevenue(params)
    if (res.code === '00' && res.data) {
      branchData.value = res.data.list || []
    }
  } catch (error) {
    // 使用模拟数据
    branchData.value = [
      { name: '铁一旗舰校', income: 2580000, refund: 125000 },
      { name: '工大旗舰校', income: 1860000, refund: 98000 },
      { name: '青羊分校', income: 1240000, refund: 102000 }
    ]
  }
  nextTick(() => {
    initBranchChart()
  })
}

async function loadSubjectData(params) {
  try {
    const res = await getSubjectRevenue(params)
    if (res.code === '00' && res.data) {
      subjectData.value = res.data.list || []
    }
  } catch (error) {
    // 使用模拟数据
    subjectData.value = [
      { name: '铁一旗舰校', primary: 320000, middle: 680000, high: 1580000 },
      { name: '工大旗舰校', primary: 280000, middle: 520000, high: 1060000 },
      { name: '青羊分校', primary: 180000, middle: 420000, high: 640000 }
    ]
  }
  nextTick(() => {
    initSubjectChart()
  })
}

async function loadCourseTypeData(params) {
  try {
    const res = await getCourseTypeRevenue(params)
    if (res.code === '00' && res.data) {
      courseTypeData.value = res.data.list || []
    }
  } catch (error) {
    // 使用模拟数据
    courseTypeData.value = [
      { name: '铁一旗舰校', class: 1580000, oneToOne: 1000000 },
      { name: '工大旗舰校', class: 1160000, oneToOne: 700000 },
      { name: '青羊分校', class: 760000, oneToOne: 480000 }
    ]
  }
  nextTick(() => {
    initCourseTypeChart()
  })
}

async function loadSeasonData(params) {
  try {
    const res = await getSeasonRevenue(params)
    if (res.code === '00' && res.data) {
      seasonData.value = res.data.list || []
    }
  } catch (error) {
    // 使用模拟数据
    seasonData.value = [
      { name: '铁一旗舰校', spring: 520000, summer: 680000, autumn: 780000, winter: 600000 },
      { name: '工大旗舰校', spring: 380000, summer: 480000, autumn: 560000, winter: 440000 },
      { name: '青羊分校', spring: 240000, summer: 320000, autumn: 380000, winter: 300000 }
    ]
  }
  nextTick(() => {
    initSeasonChart()
  })
}

async function loadSignUpCategoryData(params) {
  try {
    const res = await getSignUpCategory(params)
    if (res.code === '00' && res.data) {
      signUpCategoryData.value = res.data.list || []
    }
  } catch (error) {
    // 使用模拟数据
    signUpCategoryData.value = [
      { name: '铁一旗舰校', newSign: 186, expand: 68, renew: 95 },
      { name: '工大旗舰校', newSign: 124, expand: 42, renew: 68 },
      { name: '青羊分校', newSign: 82, expand: 28, renew: 45 }
    ]
  }
  nextTick(() => {
    initSignUpChart()
  })
}

// 初始化分校区柱状图
function initBranchChart() {
  if (!branchChartRef.value || branchData.value.length === 0) return

  if (!branchChart) {
    branchChart = echarts.init(branchChartRef.value)
  }

  const xData = branchData.value.map(item => item.name)
  const incomeData = branchData.value.map(item => item.income / 10000)
  const refundData = branchData.value.map(item => item.refund / 10000)

  branchChart.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          result += item.marker + item.seriesName + ': ' + item.value.toFixed(2) + '万<br/>'
        })
        return result
      }
    },
    legend: {
      data: ['实收金额', '实退金额'],
      bottom: 0
    },
    color: ['#22B894', '#EE6666'],
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'value',
      name: '金额（万元）',
      axisLabel: {
        formatter: '{value}'
      }
    },
    series: [
      {
        name: '实收金额',
        type: 'bar',
        data: incomeData,
        barWidth: '30%',
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      },
      {
        name: '实退金额',
        type: 'bar',
        data: refundData,
        barWidth: '30%',
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    }
  })
}

// 初始化学部堆叠图
function initSubjectChart() {
  if (!subjectChartRef.value || subjectData.value.length === 0) return

  if (!subjectChart) {
    subjectChart = echarts.init(subjectChartRef.value)
  }

  const xData = subjectData.value.map(item => item.name)
  const primaryData = subjectData.value.map(item => item.primary / 10000)
  const middleData = subjectData.value.map(item => item.middle / 10000)
  const highData = subjectData.value.map(item => item.high / 10000)

  subjectChart.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        let total = 0
        params.forEach(item => { total += item.value })
        let result = params[0].name + '<br/>'
        params.forEach(item => {
          result += item.marker + item.seriesName + ': ' + item.value.toFixed(2) + '万<br/>'
        })
        result += '<hr style="margin: 3px 0">合计: ' + total.toFixed(2) + '万'
        return result
      }
    },
    legend: {
      data: subjectLegend,
      bottom: 0
    },
    color: subjectColors,
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'value',
      name: '金额（万元）'
    },
    series: [
      {
        name: '小学',
        type: 'bar',
        stack: 'total',
        data: primaryData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '初中',
        type: 'bar',
        stack: 'total',
        data: middleData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '高中',
        type: 'bar',
        stack: 'total',
        data: highData,
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    }
  })
}

// 初始化班课/一对一分类图
function initCourseTypeChart() {
  if (!courseTypeChartRef.value || courseTypeData.value.length === 0) return

  if (!courseTypeChart) {
    courseTypeChart = echarts.init(courseTypeChartRef.value)
  }

  const xData = courseTypeData.value.map(item => item.name)
  const classData = courseTypeData.value.map(item => item.class / 10000)
  const oneToOneData = courseTypeData.value.map(item => item.oneToOne / 10000)

  courseTypeChart.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: courseTypeLegend,
      bottom: 0
    },
    color: courseTypeColors,
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'value',
      name: '金额（万元）'
    },
    series: [
      {
        name: '班课',
        type: 'bar',
        stack: 'total',
        data: classData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '1对1',
        type: 'bar',
        stack: 'total',
        data: oneToOneData,
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    }
  })
}

// 初始化季节分类图
function initSeasonChart() {
  if (!seasonChartRef.value || seasonData.value.length === 0) return

  if (!seasonChart) {
    seasonChart = echarts.init(seasonChartRef.value)
  }

  const xData = seasonData.value.map(item => item.name)
  const springData = seasonData.value.map(item => item.spring / 10000)
  const summerData = seasonData.value.map(item => item.summer / 10000)
  const autumnData = seasonData.value.map(item => item.autumn / 10000)
  const winterData = seasonData.value.map(item => item.winter / 10000)

  seasonChart.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: seasonLegend,
      bottom: 0
    },
    color: seasonColors,
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'value',
      name: '金额（万元）'
    },
    series: [
      {
        name: '春季',
        type: 'bar',
        stack: 'total',
        data: springData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '暑期',
        type: 'bar',
        stack: 'total',
        data: summerData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '秋季',
        type: 'bar',
        stack: 'total',
        data: autumnData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '寒假',
        type: 'bar',
        stack: 'total',
        data: winterData,
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    }
  })
}

// 初始化报名分类图
function initSignUpChart() {
  if (!signUpChartRef.value || signUpCategoryData.value.length === 0) return

  if (!signUpChart) {
    signUpChart = echarts.init(signUpChartRef.value)
  }

  const xData = signUpCategoryData.value.map(item => item.name)
  const newSignData = signUpCategoryData.value.map(item => item.newSign)
  const expandData = signUpCategoryData.value.map(item => item.expand)
  const renewData = signUpCategoryData.value.map(item => item.renew)

  signUpChart.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: signUpCategoryLegend,
      bottom: 0
    },
    color: signUpCategoryColors,
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { interval: 0, rotate: 30 }
    },
    yAxis: {
      type: 'value',
      name: '人数'
    },
    series: [
      {
        name: '新签',
        type: 'bar',
        stack: 'total',
        data: newSignData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '扩科',
        type: 'bar',
        stack: 'total',
        data: expandData,
        itemStyle: { borderRadius: [0, 0, 0, 0] }
      },
      {
        name: '续费',
        type: 'bar',
        stack: 'total',
        data: renewData,
        itemStyle: { borderRadius: [4, 4, 0, 0] }
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    }
  })
}

// 页面加载时初始化
onMounted(() => {
  if(route?.query?.token) {
      localStorage.setItem('token', route?.query?.token)
    }
  dateRange.value = getDateRangeByType('currentMonth')
  loadAllData()
  // 添加iframe消息监听
  window.addEventListener('message', handleIframeMessage)
})

// 监听窗口变化，重绘图表
const resizeCharts = () => {
  branchChart?.resize()
  subjectChart?.resize()
  courseTypeChart?.resize()
  seasonChart?.resize()
  signUpChart?.resize()
}

// iframe消息处理：接收web端导出参数请求
const handleIframeMessage = (event) => {
  const { data } = event
  if (data && data.type === 'getExportParams') {
    // 获取当前选中的参数
    const orgId = currentOrg.value === 'all' ? '' : currentOrg.value
    const startDate = dateRange.value?.[0] || ''
    const endDate = dateRange.value?.[1] || ''

    // 向web端返回参数
    if (event.source && event.source.postMessage) {
      event.source.postMessage(
        {
          type: 'exportParams',
          orgId,
          startDate,
          endDate
        },
        '*'
      )
    }
  }
}

onUnmounted(() => {
  window.removeEventListener('resize', resizeCharts)
  window.removeEventListener('message', handleIframeMessage)
})
</script>

<style scoped lang="scss">
.revenue-page {
  background: #f5f6fa;
  min-height: 100vh;
  padding-bottom: 20px;
  height: 100vh;
  overflow-y: scroll;
}

.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 12px 12px;
  background: #fff;
}

.org-select-card {
  flex: 1;
}

.custom-dropdown-menu {
  background: transparent;
  box-shadow: none;
}

.custom-dropdown-title {
  color: #222;
  font-size: 16px;
  font-weight: 600;
}

.date-filter-btn {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: #f5f6fa;
  border-radius: 16px;
  font-size: 13px;
  color: #666;

  .date-text {
    margin-right: 4px;
  }
}

// 当月统计卡片
.month-summary-cards {
  display: flex;
  padding: 12px;
  gap: 12px;
}

.summary-card {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &.income {
    border-left: 3px solid #22B894;
  }

  &.refund {
    border-left: 3px solid #EE6666;
  }
}

.card-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.card-value {
  display: flex;
  align-items: baseline;

  .value-number {
    font-size: 24px;
    font-weight: 700;
    color: #222;
  }

  .value-unit {
    font-size: 12px;
    color: #999;
    margin-left: 2px;
  }
}

.card-compare {
  margin-top: 6px;
  font-size: 12px;

  .up {
    color: #22B894;
  }

  .down {
    color: #EE6666;
  }

  .compare-text {
    color: #999;
    margin-left: 4px;
  }
}

// 统计区域
.period-stats-section {
  background: #fff;
  margin: 0 12px 12px;
  border-radius: 12px;
  padding: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 16px;

  .subtitle {
    font-size: 12px;
    font-weight: 400;
    color: #999;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.stat-item {
  text-align: center;
  padding: 12px;
  background: #f5f6fa;
  border-radius: 8px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #22B894;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

// 图表区域
.chart-section {
  background: #fff;
  margin: 0 12px 12px;
  border-radius: 12px;
  padding: 16px;
}

.chart-container {
  height: 280px;
  width: 100%;
}

.chart-legend-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #666;

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
  }
}

// 加载状态
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

// 日期筛选弹窗
.date-filter-popup {
  padding: 16px;

  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .cancel-btn {
      color: #999;
      font-size: 14px;
    }

    .popup-title {
      font-size: 16px;
      font-weight: 600;
      color: #222;
    }

    .confirm-btn {
      color: #22B894;
      font-size: 14px;
    }
  }

  .quick-date-btns {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;

    .quick-btn {
      padding: 8px 16px;
      background: #f5f6fa;
      border-radius: 16px;
      font-size: 13px;
      color: #666;

      &.active {
        background: #e6f7f3;
        color: #22B894;
      }
    }
  }

  .custom-date-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: #f5f6fa;
    border-radius: 8px;
    color: #666;
    font-size: 14px;
    margin-bottom: 12px;

    span {
      margin-left: 6px;
    }
  }

  .current-range {
    text-align: center;
    font-size: 14px;
    color: #222;
  }
}

// 下拉菜单样式覆盖
:deep(.van-dropdown-menu__bar) {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
